import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { isAxiosError } from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FulfillmentAdminClient,
  type ManualCarrierCancelPatch,
  type ManualCarrierCancelRow,
  type ManualCarrierCancelWhy,
} from '@/infrastructure/FulfillmentAdminClient';
import { OperatorIdentity } from '@/infrastructure/OperatorIdentity';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { useAuth } from '@/providers/AuthProvider';
import { ROUTES } from '@/routes/routes';
import { describeFailure } from '@/utils/fulfillmentErrors';
import { kstDay, kstHourMinute, kstMonthDayMinute } from '@/utils/fulfillmentTime';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import { useFulfillmentToast } from '@/pages/fulfillment/components/useFulfillmentToast';
import { RefreshIcon } from '@/pages/fulfillment/components/UltimateIcons';
import '../wireframe.css';
import '../ultimateDesign.css';
import './manualCarrierCancels.css';

/**
 * The most rows the server answers per list (its maximum); the screen says so when a list fills it rather than implying
 * it holds everything.
 */
export const MANUAL_CANCELS_LIMIT = 500;

/** Why FF2 could not cancel the waybill itself, in the desk's words. */
export const MANUAL_CANCEL_WHY_LABEL: Record<ManualCarrierCancelWhy, string> = {
  EXTERNAL_WAYBILL: 'Booked outside OMS',
  CARRIER_HAS_NO_CANCEL: 'Carrier has no cancel call',
  RETRY_LIMIT_REACHED: 'Retry limit reached',
  FAILED_FINAL: 'Carrier refused the cancel',
};

const ORDER_STATUS_CHIP: Record<string, { label: string; chip: string }> = {
  CANCELLED: { label: 'Cancelled', chip: 'st-cancelled' },
  RESOLVED: { label: 'Closed', chip: 'st-completed' },
};

type Tab = 'OPEN' | 'DONE';

const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: 'OPEN', label: 'Open' },
  { key: 'DONE', label: 'Done' },
];

/** What a list read came back as: the rows, a server that has no such list yet, or a failure to say. */
type ListState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      open: ManualCarrierCancelRow[];
      done: ManualCarrierCancelRow[];
      /** Either read stopped before the oldest settled order, so older rows may be missing. */
      truncated: boolean;
    }
  | { kind: 'missing' }
  | { kind: 'failed'; message: string };

/** The typed-but-unsaved assignee and note of one row. */
interface Draft {
  assignee: string;
  note: string;
}

const draftOf = (row: ManualCarrierCancelRow): Draft => ({ assignee: row.assignee ?? '', note: row.note ?? '' });

/** The server's order: newest closed first, an order with no close time last. */
const closedMs = (row: ManualCarrierCancelRow): number =>
  row.closedAt ? Date.parse(row.closedAt) : Number.NEGATIVE_INFINITY;
export const newestClosedFirst = (a: ManualCarrierCancelRow, b: ManualCarrierCancelRow): number => {
  const [left, right] = [closedMs(a), closedMs(b)];
  // Ties as the server breaks them: the higher order id first.
  return left === right ? b.orderId - a.orderId : right > left ? 1 : -1;
};

/**
 * The fields of a draft that differ from the row as saved; a cleared assignee is sent as null, so the server takes it
 * off rather than storing an empty name.
 */
export const changedFields = (row: ManualCarrierCancelRow, draft: Draft): ManualCarrierCancelPatch => {
  const patch: ManualCarrierCancelPatch = {};
  const assignee = draft.assignee.trim();
  if (assignee !== (row.assignee ?? '')) {
    patch.assignee = assignee === '' ? null : assignee;
  }
  const note = draft.note.trim();
  if (note !== (row.note ?? '')) {
    patch.note = note;
  }
  return patch;
};

/**
 * Manual Carrier Cancels (SSD-7586): cancelled or closed marketing orders whose waybill FF2 did not cancel at the carrier
 * and cannot by retrying (booked outside OMS, a carrier with no cancel call, a cancel that ran out of retries or was
 * refused for good). A person cancels each one in the carrier's own system, then ticks Done here; the assignee and a
 * note say who is on it. Every edit is written as an event on the order's own stream.
 */
export default function ManualCarrierCancelsPage() {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const { user } = useAuth();
  const operator = OperatorIdentity.current() ?? OperatorIdentity.handleOf(user?.email, user?.name) ?? '';
  const [searchParams] = useSearchParams();
  const markedOrder = Number(searchParams.get('order') ?? '') || null;
  const [tab, setTab] = useState<Tab>('OPEN');
  const [list, setList] = useState<ListState>({ kind: 'loading' });
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  /** Rows whose write is with the server; their controls hold until it answers. */
  const [saving, setSaving] = useState<ReadonlySet<number>>(new Set());
  const savingRef = useRef(new Set<number>());
  const requestRef = useRef(0);
  const { toasts, showSuccess, showError, dismiss } = useFulfillmentToast();

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setList((current) => (current.kind === 'ready' ? current : { kind: 'loading' }));
    try {
      // Both lists at once, so each tab carries its count.
      const [open, done] = await Promise.all([
        client.listManualCarrierCancels('OPEN', MANUAL_CANCELS_LIMIT),
        client.listManualCarrierCancels('DONE', MANUAL_CANCELS_LIMIT),
      ]);
      if (request === requestRef.current) {
        setList({
          kind: 'ready',
          open: open.rows,
          done: done.rows,
          truncated: open.truncated === true || done.truncated === true,
        });
        // Typing on a row still listed survives a reload; a row that left the list takes its draft with it.
        const listed = new Set([...open.rows, ...done.rows].map((row) => row.orderId));
        setDrafts((current) =>
          Object.fromEntries(Object.entries(current).filter(([orderId]) => listed.has(Number(orderId)))),
        );
      }
    } catch (error) {
      if (request !== requestRef.current) {
        return;
      }
      // A server without the list answers 404 for the route itself: a state of the screen, not a failure to report.
      setList(
        isAxiosError(error) && error.response?.status === 404
          ? { kind: 'missing' }
          : { kind: 'failed', message: describeFailure(error, 'Manual carrier cancels could not be read.') },
      );
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  // The row Order Detail linked to is brought into view once the list is on screen.
  useEffect(() => {
    if (list.kind === 'ready' && markedOrder !== null) {
      document.getElementById(`manual-cancel-${markedOrder}`)?.scrollIntoView?.({ block: 'center' });
    }
  }, [list.kind, markedOrder]);

  const rows = list.kind === 'ready' ? (tab === 'OPEN' ? list.open : list.done) : [];

  const draftFor = (row: ManualCarrierCancelRow): Draft => drafts[row.orderId] ?? draftOf(row);

  const setDraft = (row: ManualCarrierCancelRow, change: Partial<Draft>) =>
    setDrafts((current) => ({ ...current, [row.orderId]: { ...(current[row.orderId] ?? draftOf(row)), ...change } }));

  /** The answer replaces the row, and moves it between Open and Done when Done changed. */
  const place = (answer: ManualCarrierCancelRow) =>
    setList((current) => {
      if (current.kind !== 'ready') {
        return current;
      }
      const without = (items: ManualCarrierCancelRow[]) => items.filter((item) => item.orderId !== answer.orderId);
      // A row edited in place keeps its place; one moving between lists lands where the server would put it.
      const keepOrder = (items: ManualCarrierCancelRow[]) =>
        items.some((item) => item.orderId === answer.orderId)
          ? items.map((item) => (item.orderId === answer.orderId ? answer : item))
          : [...items, answer].sort(newestClosedFirst);
      return answer.done
        ? { ...current, open: without(current.open), done: keepOrder(current.done) }
        : { ...current, open: keepOrder(current.open), done: without(current.done) };
    });

  const write = async (row: ManualCarrierCancelRow, patch: ManualCarrierCancelPatch, done: string) => {
    if (savingRef.current.has(row.orderId) || Object.keys(patch).length === 0) {
      return;
    }
    savingRef.current.add(row.orderId);
    setSaving(new Set(savingRef.current));
    try {
      const answer = await client.updateManualCarrierCancel(row.orderId, patch);
      place(answer);
      setDrafts((current) => {
        const next = { ...current };
        delete next[row.orderId];
        return next;
      });
      showSuccess(done);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        // NOT_A_MANUAL_CARRIER_CANCEL: off the list since it was read (the order moved on), so the list is read again
        // rather than edited blind.
        showError(`Order ${row.orderId} is no longer on Manual carrier cancels. The list was read again.`);
        void load();
      } else {
        showError(describeFailure(error, `Order ${row.orderId} could not be saved.`));
      }
    } finally {
      savingRef.current.delete(row.orderId);
      setSaving(new Set(savingRef.current));
    }
  };

  /** Assignee and note go together, only the fields that changed. */
  const saveDraft = (row: ManualCarrierCancelRow) =>
    void write(row, changedFields(row, draftFor(row)), `Order ${row.orderId} saved.`);

  /**
   * Done is written at once, together with whatever was typed on the row and not saved yet, so ticking it never drops
   * a note. A row nobody took (neither saved nor typed) is taken by whoever ticks it.
   */
  const toggleDone = (row: ManualCarrierCancelRow, done: boolean) => {
    const draft = draftFor(row);
    const patch: ManualCarrierCancelPatch = { ...changedFields(row, draft), done };
    if (done && !row.assignee && draft.assignee.trim() === '' && operator) {
      patch.assignee = operator;
    }
    void write(row, patch, done ? `Order ${row.orderId} marked done.` : `Order ${row.orderId} is open again.`);
  };

  const onKeyDown = (row: ManualCarrierCancelRow) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveDraft(row);
    }
  };

  return (
    <div className="wms-wireframe ff-ultimate wms-manual-cancels">
      <FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />
      <div className="ptitle">
        <h2>WMS - Manual Carrier Cancels</h2>
      </div>
      <p className="mcc-intro">
        Cancelled or closed marketing orders whose waybill FF2 could not cancel at the carrier. Cancel each one in the
        carrier&apos;s own system, then tick Done.
      </p>
      <div className="psub">
        <h3>Waybills to cancel by hand</h3>
        <span className="sp" />
        <button type="button" className="btn btn-blue" disabled={list.kind === 'loading'} onClick={() => void load()}>
          <RefreshIcon />
          Refresh
        </button>
      </div>

      {list.kind === 'missing' ? (
        <div className="mcc-state" role="status">
          Manual carrier cancels are not on this server yet.
        </div>
      ) : list.kind === 'failed' ? (
        <div className="mcc-state is-error" role="alert">
          {list.message}
        </div>
      ) : (
        <>
          <div className="viewtabs" role="tablist" aria-label="Show">
            {TABS.map((entry) => (
              <button
                type="button"
                key={entry.key}
                role="tab"
                aria-selected={entry.key === tab}
                className={entry.key === tab ? 'vtab on' : 'vtab'}
                onClick={() => setTab(entry.key)}
              >
                {entry.label}{' '}
                <span className="n">
                  {list.kind === 'ready' ? (entry.key === 'OPEN' ? list.open : list.done).length : '…'}
                </span>
              </button>
            ))}
          </div>
          {list.kind === 'ready' && list.truncated && (
            <div className="mcc-limit" role="status" data-testid="manual-cancels-truncated">
              The server stopped before the oldest closed orders, so older ones may be missing from this list.
            </div>
          )}
          {/* The server cuts each list at the limit; a full list is said to be cut, not taken for all there is. */}
          {rows.length >= MANUAL_CANCELS_LIMIT && (
            <div className="mcc-limit" role="status">
              Showing the newest {MANUAL_CANCELS_LIMIT}; older ones are past the end of this list.
            </div>
          )}
          <div className="tbl-scroll">
            <table className="tbl" aria-label="Manual carrier cancels">
              <colgroup>
                <col className="col-order" />
                <col className="col-closed" />
                <col className="col-waybill" />
                <col className="col-why" />
                <col className="col-assignee" />
                <col />
                <col className="col-done" />
              </colgroup>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Closed</th>
                  <th>Carrier · Waybill</th>
                  <th>Why</th>
                  <th>Assignee</th>
                  <th>Note</th>
                  <th>Done</th>
                </tr>
              </thead>
              <tbody>
                {list.kind === 'loading' && (
                  <tr>
                    <td colSpan={7} className="mcc-empty">
                      Reading…
                    </td>
                  </tr>
                )}
                {list.kind === 'ready' && rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="mcc-empty">
                      {tab === 'OPEN' ? 'Nothing to cancel by hand.' : 'Nothing marked done yet.'}
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const draft = draftFor(row);
                  const dirty = Object.keys(changedFields(row, draft)).length > 0;
                  const busy = saving.has(row.orderId);
                  const status = ORDER_STATUS_CHIP[row.orderStatus] ?? { label: row.orderStatus, chip: '' };
                  const doneAt = kstMonthDayMinute(row.doneAt);
                  return (
                    <tr
                      key={row.orderId}
                      id={`manual-cancel-${row.orderId}`}
                      className={row.orderId === markedOrder ? 'row-hit' : undefined}
                    >
                      <td>
                        <Link className="oid" to={ROUTES.fulfillment.orderDetail(row.orderId)}>
                          #{row.orderId}
                        </Link>
                        <div>
                          <span className={`status ${status.chip}`}>{status.label}</span>
                        </div>
                      </td>
                      <td className="num">
                        {row.closedAt ? (
                          <>
                            {kstDay(row.closedAt)}
                            <div className="mcc-sub">{kstHourMinute(row.closedAt)} KST</div>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <div className="mcc-carrier">{row.carrier ?? 'Carrier not named'}</div>
                        <div className="mcc-waybill" title={row.trackingNumber ?? undefined}>
                          {row.trackingNumber ?? '—'}
                        </div>
                      </td>
                      <td>
                        <div className="mcc-why">{MANUAL_CANCEL_WHY_LABEL[row.why] ?? row.why}</div>
                        {(row.voidCode || row.voidMessage) && (
                          <div className="mcc-sub mcc-void">
                            {[row.voidCode, row.voidMessage].filter(Boolean).join(': ')}
                          </div>
                        )}
                      </td>
                      <td>
                        <input
                          className="mcc-input"
                          aria-label={`Assignee — order ${row.orderId}`}
                          value={draft.assignee}
                          placeholder={operator || 'Who is on it'}
                          maxLength={128}
                          disabled={busy}
                          onChange={(event) => setDraft(row, { assignee: event.target.value })}
                          onKeyDown={onKeyDown(row)}
                        />
                      </td>
                      <td>
                        <div className="mcc-note">
                          <input
                            className="mcc-input"
                            aria-label={`Note — order ${row.orderId}`}
                            value={draft.note}
                            placeholder="e.g. Cancelled in YUN OMS"
                            maxLength={2000}
                            disabled={busy}
                            onChange={(event) => setDraft(row, { note: event.target.value })}
                            onKeyDown={onKeyDown(row)}
                          />
                          {/* Kept in place while hidden, so the cell does not jump when the box is typed in. */}
                          <button
                            type="button"
                            className={`btn btn-sm btn-blue mcc-save${dirty ? '' : ' is-idle'}`}
                            aria-label={`Save — order ${row.orderId}`}
                            disabled={!dirty || busy}
                            onClick={() => saveDraft(row)}
                          >
                            Save
                          </button>
                        </div>
                      </td>
                      <td>
                        <label className="mcc-done">
                          <input
                            type="checkbox"
                            aria-label={`Done — order ${row.orderId}`}
                            checked={row.done}
                            disabled={busy}
                            onChange={(event) => toggleDone(row, event.target.checked)}
                          />
                          Done
                        </label>
                        {row.done && (row.doneBy || doneAt) && (
                          <div className="mcc-sub">{[row.doneBy, doneAt].filter(Boolean).join(' · ')}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
